<template>
  <div class="film-app">
    <div class="list-container" ref="listContainer" @scroll="handleScroll">
      <div class="refresh-tip" :class="{active: refreshing}">
        <svg v-if="!refreshing" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17 18V16H7V18L3 15L7 12V14H17V12L21 15L17 18Z" fill="#666"/>
        </svg>
        <div class="spinner" v-if="refreshing"></div>
        {{ refreshing ? '正在刷新...' : '下拉刷新' }}
      </div>
      
      <div class="film-list">
        <div 
          class="film-item" 
          v-for="(film, index) in filmList" 
          :key="index"
          v-show="film.remainingTime > 0"
        >
          <div class="cinema-info">
            <div class="cinema-name">{{ film.cinema }}</div>
            <div class="people-count">{{ film.people }}人</div>
          </div>
          <div class="film-info">
            <div class="film-title">{{ film.title }}</div>
            <div class="hall">{{ film.hall }}</div>
          </div>
          <div class="price-info">
            <div class="original-price">原价：¥{{ film.originalPrice }}/张</div>
            <div class="discounted-price">最高限价：¥{{ film.discountedPrice }}/张</div>
            <div class="countdown-container">
              <div class="countdown">
                <span v-if="film.remainingTime > 0">
                  {{ formatCountdown(film.remainingTime) }}
                </span>
                <span v-else>已结束</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="load-more" v-if="loadingMore">
        <div class="spinner"></div>
        加载中...
      </div>
      <div class="no-more" v-if="noMoreData">
        没有更多数据了
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'FilmList',
  data() {
    return {
      filmList: [],
      refreshing: false,
      loadingMore: false,
      noMoreData: false,
      pageNum: 0,
      pageLimit: 10,
      scrollPosition: 0,
      countdownTimer: null
    }
  },
  mounted() {
    this.fetchFilmList();
    // 启动全局倒计时定时器
    this.startCountdownTimer();
  },
  beforeUnmount() {
    // 组件销毁时清除定时器
    clearInterval(this.countdownTimer);
  },
  methods: {
    async fetchFilmList(refresh = false) {
      // 状态检查
      if (refresh) {
        if (this.refreshing) return;
        this.refreshing = true;
        this.pageNum = 1;
        this.noMoreData = false;
      } else {
        if (this.noMoreData || this.loadingMore || this.refreshing) return;
        this.loadingMore = true;
        this.pageNum++; // 上拉加载页码+1
      }

      const token = localStorage.getItem('token');
      if (!token) {
        console.error('Token 不存在!');
        this.$router.push('/h5/login'); // 重定向到登录
        return;
      }

      try {
        const response = await axios.post('/film/selected/data/filmList', {
          pageNum: this.pageNum,
          pageLimit: this.pageLimit
        }, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': localStorage.getItem('token')
          }
        });

        if (response.data.code === 'msg00000') {
          const newData = response.data.data.map(item => {
            // 计算剩余时间（秒）
            const endTime = new Date(item.biddingEndtime).getTime();
            const now = Date.now();
            const remainingTime = Math.max(0, Math.floor((endTime - now) / 1000));
            
            return {
              cinema: `${item.movieCityName}-${item.movieCinemaName}`,
              people: item.nowBiddingNum,
              title: item.movieName,
              hall: item.movieHallName,
              originalPrice: item.salePrice,
              discountedPrice: item.discountPriceUp,
              biddingEndtime: item.biddingEndtime, // 保留原始时间
              remainingTime // 剩余秒数
            };
          });

          // 更新列表
          if (refresh) {
            this.filmList = newData;
          } else {
            this.filmList = [...this.filmList, ...newData];
          }

          // 检测数据是否结束
          this.noMoreData = newData.length < this.pageLimit;
          
        } else {
          throw new Error(response.data.message);
        }
      } catch (error) {
        console.error('请求失败:', error);
        // 回退页码（仅限加载操作）
        if (!refresh) this.pageNum = Math.max(1, this.pageNum - 1);
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          this.$router.push('/h5/login');
        }
      } finally {
        this.refreshing = false;
        this.loadingMore = false;
      }
    },
    
    // 启动全局倒计时定时器
    startCountdownTimer() {
      this.countdownTimer = setInterval(() => {
        // 遍历所有电影项更新倒计时
        this.filmList.forEach(film => {
          if (film.remainingTime > 0) {
            film.remainingTime--;
          }
        });
        
        // 移除剩余时间为0的条目
        this.filmList = this.filmList.filter(film => film.remainingTime > 0);
      }, 1000);
    },
    
    // 格式化倒计时显示
    formatCountdown(seconds) {
      if (seconds <= 0) return '00:00';
      
      // const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;
      
      return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },
    
    handleScroll(e) {
      const container = e.target;
      // 下拉刷新（需确保容器支持负滚动）
      if (container.scrollTop < -100 && !this.refreshing) {
        this.fetchFilmList(true);
      }
      // 上拉加载
      const scrollBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      if (scrollBottom < 50 && !this.loadingMore && !this.noMoreData) {
        this.fetchFilmList(); // 触发加载
      }
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

body {
  background-color: #f5f7fa;
  color: #333;
  overflow: hidden;
}

.film-app {
  max-width: 500px;
  margin: 0 auto;
  background: white;
  height: 100vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 0 20px rgba(0,0,0,0.05);
}

.list-container {
  flex: 1;
  overflow-y: auto;
  position: relative;
  padding-top: 10px;
}

.refresh-tip {
  position: absolute;
  top: -50px;
  left: 0;
  right: 0;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #666;
  background: rgba(255,255,255,0.9);
  z-index: 2;
  transition: top 0.3s;
}

.refresh-tip.active {
  top: 0;
}

.refresh-tip svg {
  margin-right: 8px;
  transform: rotate(180deg);
}

.film-list {
  padding: 0 15px 15px;
}

.film-item {
  background: white;
  border-radius: 12px;
  padding: 15px;
  margin-bottom: 15px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
  transition: all 0.3s;
  border: 1px solid #f0f0f0;
  position: relative;
}

.film-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0,0,0,0.08);
}

.cinema-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  align-items: center;
}

.cinema-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 10px;
}

.people-count {
  background: #f0f7ff;
  color: #1890ff;
  font-size: 12px;
  padding: 3px 8px;
  border-radius: 20px;
  font-weight: 500;
  flex-shrink: 0;
}

.film-info {
  display: flex;
  margin-bottom: 12px;
  font-size: 14px;
  flex-wrap: wrap;
}

.film-title {
  color: #333;
  font-weight: 500;
  margin-right: 10px;
}

.hall {
  color: #666;
}

.price-info {
  display: flex;
  flex-wrap: wrap;
  font-size: 14px;
  margin-bottom: 8px;
}

.original-price {
  color: #999;
  text-decoration: line-through;
  margin-right: 15px;
}

.discounted-price {
  color: #ff5e62;
  font-weight: 500;
}

.countdown-container {
  background: rgba(253, 130, 10, 0.11); 
  border-radius: 33px; 
  padding: 6px 12px;
  display: inline-flex; /* 宽度自适应内容 */
  justify-content: center; /* 内容居中 */
  margin-left: auto; /* 靠右显示 */
  margin-top: 8px; /* 与上方内容保持距离 */
}

.countdown {
  font-size: 22px; /* 更大字体 */
  font-weight: 700; /* 粗体 */
  color: #fd820a; /* 橙色 */
  letter-spacing: 0.5px; /* 字母间距 */
}

/* 加载更多样式 */
.load-more, .no-more {
  text-align: center;
  padding: 20px;
  color: #999;
  font-size: 14px;
  background: #fafafa;
  border-top: 1px solid #f0f0f0;
}

.spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 94, 98, 0.3);
  border-radius: 50%;
  border-top-color: #ff5e62;
  animation: spin 1s ease-in-out infinite;
  margin-right: 10px;
  vertical-align: middle;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 响应式调整 */
@media (max-width: 480px) {
  .film-app {
    max-width: 100%;
  }
  
  .film-item {
    padding: 12px;
  }
  
  .cinema-name {
    font-size: 15px;
  }
  
  .countdown {
    font-size: 20px; /* 移动端稍小字体 */
  }
  
  .buy-btn {
    padding: 8px 18px;
    font-size: 14px;
  }
  
  .hall {
    margin-top: 5px;
    width: 100%;
  }
}
</style>